import { Box, Button, CircularProgress, Fade, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'

import GoogleSignInButton from '../../components/auth/GoogleSignInButton'
import { useAuth } from '../../contexts/AuthContext'
import { useReaderLightPalette } from '../../hooks/useReaderLightPalette'
import { InvitationConfirmResponse, InvitationPublic, apiService } from '../../services/api'
import { READER_PAPER_BACKGROUND_SX } from '../../styles/paperBackground'

const ChapterOrnament: React.FC = () => (
  <svg
    width="120"
    height="24"
    viewBox="0 0 120 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.4 }}
  >
    <path d="M60 4 L66 12 L60 20 L54 12 Z" fill="var(--ornament-fill)" />
    <path
      d="M50 12 Q40 6 28 12 Q16 18 4 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M70 12 Q80 6 92 12 Q104 18 116 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <ellipse
      cx="28"
      cy="10"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-30 28 10)"
      opacity="0.6"
    />
    <ellipse cx="16" cy="15" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(20 16 15)" opacity="0.5" />
    <ellipse cx="92" cy="10" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(30 92 10)" opacity="0.6" />
    <ellipse
      cx="104"
      cy="15"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-20 104 15)"
      opacity="0.5"
    />
  </svg>
)

const StarCluster: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.35 }}
  >
    <circle cx="20" cy="8" r="1.5" fill="var(--ornament-fill)" />
    <circle cx="12" cy="20" r="1" fill="var(--ornament-stroke)" />
    <circle cx="28" cy="18" r="1.2" fill="var(--ornament-fill)" />
    <circle cx="18" cy="30" r="0.8" fill="var(--ornament-stroke)" />
    <circle cx="30" cy="28" r="1" fill="var(--ornament-fill)" />
  </svg>
)

type Step = 'loading' | 'identity' | 'message' | 'signin' | 'error' | 'claimed' | 'not-found'

const BOOK_FONT = "'SBL Hebrew', 'Times New Roman', serif"

const InviteLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { googleLogin, isAuthenticated } = useAuth()

  // This is the reader's first page of the book, not a form in an app.
  useReaderLightPalette()

  const [step, setStep] = useState<Step>('loading')
  const [invitation, setInvitation] = useState<InvitationPublic | null>(null)
  const [confirmData, setConfirmData] = useState<InvitationConfirmResponse | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  useEffect(() => {
    if (!token) {
      setStep('not-found')
      return
    }

    apiService
      .getInvitationByToken(token)
      .then((res) => {
        if (res.success) {
          const inv = res.data
          setInvitation(inv)
          if (inv.status === 'claimed') {
            setStep('claimed')
          } else if (inv.status === 'expired') {
            setStep('error')
          } else {
            setStep('identity')
          }
        } else {
          setStep('not-found')
        }
      })
      .catch(() => {
        setStep('not-found')
      })
  }, [token])

  const handleConfirmIdentity = useCallback(async () => {
    if (!token) {
      return
    }
    try {
      const res = await apiService.confirmInvitation(token)
      if (res.success) {
        setConfirmData(res.data)
        if (res.data.personalMessage) {
          setStep('message')
        } else {
          setStep('signin')
        }
      }
    } catch {
      toast.error('Something went wrong')
    }
  }, [token])

  const handleDeny = useCallback(() => {
    setStep('error')
  }, [])

  /**
   * A claimed character lands on their own page rather than on the homepage — it is
   * the one place in the book where they speak for themselves, and where they name a
   * wallet if they want one. From there the book is one link away.
   */
  const destinationAfterClaim = useCallback(
    () =>
      invitation?.type === 'character' && invitation.bookId && invitation.characterTagId
        ? // Their own page first: the one place in the book where they speak rather than
          // being written, and where they choose what a reader can send them.
          `/character/${invitation.bookId}/${invitation.characterTagId}`
        : '/',
    [invitation?.type, invitation?.bookId],
  )

  const handleContinueToSignIn = useCallback(() => {
    setStep('signin')
  }, [])

  const handleGoogleSuccess = useCallback(
    async (credential: string) => {
      if (!token) {
        return
      }
      setSigningIn(true)
      try {
        const success = await googleLogin(credential, token)
        if (success) {
          navigate(destinationAfterClaim())
        }
      } finally {
        setSigningIn(false)
      }
    },
    [token, googleLogin, navigate, destinationAfterClaim],
  )

  useEffect(() => {
    if (isAuthenticated && step === 'signin') {
      navigate(destinationAfterClaim())
    }
  }, [isAuthenticated, step, navigate, destinationAfterClaim])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // The same paper as the book itself — grain and water-stain blooms, not a flat fill.
        ...READER_PAPER_BACKGROUND_SX,
        px: 3,
      }}
      dir="rtl"
    >
      {step === 'loading' && (
        <Fade in>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <StarCluster size={60} />
            <CircularProgress size={32} sx={{ color: 'var(--reader-spinner)' }} />
          </Box>
        </Fade>
      )}

      {step === 'not-found' && (
        <Fade in>
          <Box sx={{ textAlign: 'center' }}>
            <StarCluster size={50} />
            <Typography sx={{ fontFamily: BOOK_FONT, color: 'var(--reader-text-muted)', fontSize: '1.1rem', mt: 3 }}>
              ההזמנה לא נמצאה.
            </Typography>
          </Box>
        </Fade>
      )}

      {step === 'claimed' && (
        <Fade in>
          <Box sx={{ textAlign: 'center' }}>
            <StarCluster size={50} />
            <Typography sx={{ fontFamily: BOOK_FONT, color: 'var(--reader-text-muted)', fontSize: '1.1rem', mt: 3 }}>
              ההזמנה הזו כבר מומשה.
            </Typography>
            <Button onClick={() => navigate('/login')} sx={{ mt: 3, fontFamily: BOOK_FONT }}>
              התחברות
            </Button>
          </Box>
        </Fade>
      )}

      {step === 'error' && (
        <Fade in>
          <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
            <StarCluster size={50} />
            <Typography sx={{ fontFamily: BOOK_FONT, color: 'var(--reader-text-muted)', fontSize: '1.1rem', mt: 3 }}>
              ההזמנה הזו יועדה למישהו אחר.
            </Typography>
          </Box>
        </Fade>
      )}

      {(step === 'identity' || step === 'message' || step === 'signin') && invitation && (
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 500,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Title page aesthetic */}
          <Box
            sx={{
              width: { xs: 140, md: 180 },
              height: { xs: 140, md: 180 },
              mb: 4,
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, var(--reader-star-glow) 0%, transparent 70%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StarCluster size={60} />
          </Box>

          <Typography
            sx={{
              fontFamily: BOOK_FONT,
              fontWeight: 'normal',
              fontSize: { xs: '1.6rem', md: '2rem' },
              color: 'var(--reader-text-primary)',
              lineHeight: 1.4,
              letterSpacing: '0.04em',
              mb: 2,
            }}
          >
            המניפסט האנושי האחרון
          </Typography>

          <ChapterOrnament />

          <Typography
            sx={{
              fontFamily: BOOK_FONT,
              fontSize: '0.9rem',
              color: 'var(--reader-text-submuted)',
              mt: 2,
              mb: 5,
              letterSpacing: '0.06em',
            }}
          >
            johnjohn
          </Typography>

          {/* Step: Identity confirmation */}
          {step === 'identity' && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <Typography
                  sx={{
                    fontFamily: BOOK_FONT,
                    fontSize: { xs: '1.4rem', md: '1.7rem' },
                    color: 'var(--reader-text-primary)',
                    fontWeight: 'normal',
                  }}
                >
                  {invitation.recipientName}?
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleConfirmIdentity}
                    sx={{
                      fontFamily: BOOK_FONT,
                      fontSize: '1rem',
                      px: 4,
                      py: 1,
                      borderColor: 'var(--reader-text-submuted)',
                      color: 'var(--reader-text-primary)',
                      '&:hover': {
                        borderColor: 'var(--reader-text-primary)',
                        bgcolor: 'var(--reader-hover)',
                      },
                    }}
                  >
                    כן
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleDeny}
                    sx={{
                      fontFamily: BOOK_FONT,
                      fontSize: '1rem',
                      px: 4,
                      py: 1,
                      color: 'var(--reader-text-submuted)',
                    }}
                  >
                    לא
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step: Personal message */}
          {step === 'message' && confirmData?.personalMessage && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, maxWidth: 450 }}>
                <Typography
                  sx={{
                    fontFamily: BOOK_FONT,
                    fontSize: '1.1rem',
                    color: 'var(--reader-text-primary)',
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  {confirmData.personalMessage}
                </Typography>

                <Button
                  variant="outlined"
                  onClick={handleContinueToSignIn}
                  sx={{
                    fontFamily: BOOK_FONT,
                    fontSize: '1rem',
                    px: 4,
                    py: 1,
                    mt: 2,
                    borderColor: 'var(--reader-text-submuted)',
                    color: 'var(--reader-text-primary)',
                    '&:hover': {
                      borderColor: 'var(--reader-text-primary)',
                      bgcolor: 'var(--reader-hover)',
                    },
                  }}
                >
                  המשך
                </Button>
              </Box>
            </Fade>
          )}

          {/* Step: Sign in */}
          {step === 'signin' && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <Typography
                  sx={{
                    fontFamily: BOOK_FONT,
                    fontSize: '1rem',
                    color: 'var(--reader-text-secondary)',
                    mb: 1,
                  }}
                >
                  עכשיו לוחצים כאן
                </Typography>
                <Typography sx={{ fontSize: '1.2rem', color: 'var(--reader-text-submuted)', lineHeight: 1, mb: 1 }}>
                  ↓
                </Typography>

                {signingIn ? (
                  <CircularProgress size={32} sx={{ color: 'var(--reader-spinner)' }} />
                ) : (
                  <GoogleSignInButton onSuccess={handleGoogleSuccess} text="continue_with" />
                )}
              </Box>
            </Fade>
          )}
        </Box>
      )}
    </Box>
  )
}

export default InviteLanding
